import { trackNoticeRun } from 'components/BackgroundRuns'
import { TagMatch } from 'components/TagMatchToggle'
import { TagScopePicker } from 'components/TagScopePicker'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  cancelRateNoticeRun,
  downloadRateNotices,
  getRateNoticeStatus,
  previewRateNotices,
  RateNoticeScope,
  RateNoticeSkippedRow,
  RateNoticeTargetRow,
  startRateNoticeRun,
} from 'services'
import { Button } from 'stories/components'

const FIELDS: Array<{ key: 'rate' | 'payment' | 'escrow'; label: string }> = [
  { key: 'rate', label: 'Interest Rate' },
  { key: 'payment', label: 'P&I Payment' },
  { key: 'escrow', label: 'Escrow' },
]

const SOURCES: Array<{ key: string; label: string }> = [
  { key: 'arm', label: 'ARM adjustment' },
  { key: 'manual', label: 'Entered by hand' },
  { key: 'stepRate', label: 'Step rate' },
  { key: 'escrowAnalysis', label: 'Escrow analysis' },
  { key: 'recast', label: 'Payment recalculated' },
]

/**
 * Today, and the day `days` from it, as the date inputs want them.
 *
 * Built from the local parts rather than `toISOString()`, which is UTC: for the last hours of
 * every working day that reads as tomorrow, and the window would open on a date the servicer
 * has not reached yet.
 */
const isoDay = (days = 0) => {
  const date = new Date()
  date.setDate(date.getDate() + days)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * Rate and Payment Change Notices for every loan with a change in the window, rather than one
 * loan at a time from Rate & Payment Changes.
 *
 * The scope is previewed as it is set - the review list is a database query, no documents are
 * produced - so the number on the Send button is the number of borrowers who will be mailed.
 */
export default function RateChangeNoticesModal({ onClose, allTags }: { onClose: () => void; allTags: string[] }) {
  const [from, setFrom] = useState(isoDay())
  const [to, setTo] = useState(isoDay(60))
  const [fields, setFields] = useState<Record<string, boolean>>({ rate: true, payment: true, escrow: true })
  const [sources, setSources] = useState<Record<string, boolean>>(
    Object.fromEntries(SOURCES.map((s) => [s.key, true])),
  )
  const [tags, setTags] = useState<string[]>([])
  const [tagMatch, setTagMatch] = useState<TagMatch>('any')
  const [excludeTags, setExcludeTags] = useState<string[]>([])
  const [email, setEmail] = useState(true)
  const [includeAlreadyEmailed, setIncludeAlreadyEmailed] = useState(false)
  const [regenerate, setRegenerate] = useState(false)

  const [previewing, setPreviewing] = useState(true)
  const [targets, setTargets] = useState<RateNoticeTargetRow[]>([])
  const [scopeSkipped, setScopeSkipped] = useState<RateNoticeSkippedRow[]>([])

  const [phase, setPhase] = useState<'form' | 'processing' | 'done'>('form')
  const [progress, setProgress] = useState<{ handled: number; total: number }>({ handled: 0, total: 0 })
  const [result, setResult] = useState<any>(null)
  const [jobId, setJobId] = useState<string>('')
  const [downloading, setDownloading] = useState(false)
  // A ref, not state: the poll closure is created once and must see the live value.
  const handedOff = useRef(false)
  // -1 once the run is working; >= 0 is how many portfolio runs are ahead of it.
  const [queuedBehind, setQueuedBehind] = useState(-1)

  const pickedFields = FIELDS.filter((f) => fields[f.key]).map((f) => f.key)
  const pickedSources = SOURCES.filter((s) => sources[s.key]).map((s) => s.key)

  const scope = useCallback(
    (): RateNoticeScope => ({
      from,
      to,
      fields: pickedFields,
      // Every source selected is the same scope as naming none, and shorter to read in the log.
      sources: pickedSources.length === SOURCES.length ? undefined : pickedSources,
      tags,
      tagMatch,
      excludeTags,
      includeAlreadyEmailed,
      regenerate,
      email,
    }),
    [from, to, pickedFields.join(), pickedSources.join(), tags, tagMatch, excludeTags, includeAlreadyEmailed, regenerate, email],
  )

  // The review list follows the scope. Debounced, because every keystroke in a date field is a
  // scope change and this is a portfolio-wide query.
  useEffect(() => {
    if (phase !== 'form') return
    if (!pickedFields.length || !pickedSources.length) {
      setTargets([])
      setScopeSkipped([])
      setPreviewing(false)
      return
    }
    setPreviewing(true)
    const timer = setTimeout(() => {
      previewRateNotices(scope())
        .then((res) => {
          setTargets(res?.targets || [])
          setScopeSkipped(res?.skipped || [])
        })
        .catch(() => toast('Could not list the notices in scope.', { type: 'error' }))
        .finally(() => setPreviewing(false))
    }, 400)
    return () => clearTimeout(timer)
  }, [scope, phase])

  const onGenerate = async () => {
    setPhase('processing')
    setProgress({ handled: 0, total: 0 })
    const start: any = await startRateNoticeRun(scope()).catch(() => null)
    const id = start?.jobId
    if (!id) {
      setPhase('form')
      toast('Could not start the run.', { type: 'error' })
      return
    }
    setJobId(id)
    const poll = async () => {
      if (handedOff.current) return
      const st: any = await getRateNoticeStatus(id).catch(() => null)
      if (!st || st.success === 0) {
        setPhase('form')
        toast(st?.message || 'Lost track of the run.', { type: 'error' })
        return
      }
      setQueuedBehind(st.queued ? st.queuedBehind ?? 0 : -1)
      setProgress({ handled: st.handled || 0, total: st.total || 0 })
      if (st.done) {
        if (st.error) {
          setPhase('form')
          toast(st.error, { type: 'error' })
        } else {
          setResult(st.result)
          setPhase('done')
        }
        return
      }
      setTimeout(poll, 1000)
    }
    poll()
  }

  /** Stop the run. Notices already sent or filed are kept, so this closes rather than pretending. */
  const onCancelRun = async () => {
    if (!jobId) return
    handedOff.current = true
    setPhase('form')
    await cancelRateNoticeRun(jobId).catch(() => null)
    toast('Stopping the run. Notices already sent or filed are kept.', { type: 'warning' })
    onClose()
  }

  /** Hand the run to the background tray and close. The run carries on regardless. */
  const onRunInBackground = () => {
    if (!jobId) return
    handedOff.current = true
    trackNoticeRun(jobId, 'Rate & Payment Change Notices', 'rate')
    toast('Running in the background. You will be notified when it is ready.', { type: 'info' })
    onClose()
  }

  const onDownload = async () => {
    setDownloading(true)
    try {
      await downloadRateNotices(jobId)
    } catch {
      toast('No combined document was produced for this run.', { type: 'error' })
    } finally {
      setDownloading(false)
    }
  }

  const pct = progress.total ? Math.min(100, Math.round((progress.handled / progress.total) * 100)) : 0
  const generated = result?.generated || []
  const skipped = result?.skipped || []
  const alreadySent = targets.filter((t) => t.alreadyEmailed).length

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-[720px] max-w-[95vw] flex-col rounded-lg bg-white p-5 shadow-xl">
        <h2 className="mb-1 text-lg font-bold">Rate &amp; Payment Change Notices</h2>

        {phase === 'form' && (
          <>
            <p className="mb-3 text-sm text-gray-500">
              Files - and, when you choose to, emails - the borrower&apos;s notice for every scheduled rate, payment
              and escrow change in the window. Changes dated the same day on one loan make a single letter, exactly
              as they do on the loan&apos;s own Rate &amp; Payment Changes screen.
            </p>
            <div className="flex-1 overflow-auto">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="text-sm text-gray-600">
                  Effective From
                  <input
                    type="date"
                    className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                  />
                </label>
                <label className="text-sm text-gray-600">
                  Effective Through
                  <input
                    type="date"
                    className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                  />
                </label>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="text-sm text-gray-600">
                  Changes to
                  <div className="mt-1 flex flex-col gap-1">
                    {FIELDS.map((f) => (
                      <label key={f.key} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!fields[f.key]}
                          onChange={(e) => setFields((s) => ({ ...s, [f.key]: e.target.checked }))}
                        />
                        {f.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  Where the change came from
                  <div className="mt-1 flex flex-col gap-1">
                    {SOURCES.map((s) => (
                      <label key={s.key} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!sources[s.key]}
                          onChange={(e) => setSources((prev) => ({ ...prev, [s.key]: e.target.checked }))}
                        />
                        {s.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-3 text-sm text-gray-600">
                Delivery
                <div className="mt-1 flex flex-col gap-1">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked disabled />
                    File to each loan&apos;s documents
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
                    Email borrowers
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={includeAlreadyEmailed}
                      onChange={(e) => setIncludeAlreadyEmailed(e.target.checked)}
                    />
                    Include changes whose notice already went out
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={regenerate} onChange={(e) => setRegenerate(e.target.checked)} />
                    Produce a fresh notice even where one is already filed
                  </label>
                </div>
              </div>

              <div className="mt-4">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    In Scope {previewing ? '' : `(${targets.length.toLocaleString()})`}
                  </span>
                  {!previewing && scopeSkipped.length > 0 && (
                    <span className="text-[11px] text-amber-700">
                      {scopeSkipped.length.toLocaleString()} passed over (on Hold, not being serviced, or already
                      sent)
                    </span>
                  )}
                </div>
                {previewing ? (
                  <p className="py-6 text-center text-sm text-gray-400">Checking the portfolio…</p>
                ) : !pickedFields.length || !pickedSources.length ? (
                  <p className="py-6 text-center text-sm text-gray-400">
                    Pick at least one field and one source to see what is in scope.
                  </p>
                ) : targets.length === 0 ? (
                  <p className="py-6 text-center text-sm text-gray-400">
                    No loan has a change in this window that still needs a notice.
                  </p>
                ) : (
                  <div className="max-h-56 overflow-auto rounded border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Loan #</th>
                          <th className="px-3 py-1.5 text-left">Borrower</th>
                          <th className="px-3 py-1.5 text-left">Covers</th>
                          <th className="px-3 py-1.5 text-left">Source</th>
                          <th className="px-3 py-1.5 text-right">Effective</th>
                        </tr>
                      </thead>
                      <tbody>
                        {targets.map((t) => (
                          <tr key={`${t.loanNumber}-${t.effectiveDate}`} className="border-t border-slate-100">
                            <td className="px-3 py-1.5">{t.loanNo}</td>
                            <td className="px-3 py-1.5">{t.borrower}</td>
                            <td className="px-3 py-1.5">{t.fields.join(', ')}</td>
                            <td className="px-3 py-1.5 text-gray-500">{t.sources.join(', ')}</td>
                            <td className="px-3 py-1.5 text-right">{t.effectiveDate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!previewing && email && alreadySent > 0 && (
                  <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                    {alreadySent.toLocaleString()} of these borrowers have already had this notice. They will receive
                    it again.
                  </p>
                )}
              </div>

              <TagScopePicker
                className="mt-3"
                allTags={allTags}
                tags={tags}
                onChangeTags={setTags}
                tagMatch={tagMatch}
                onChangeTagMatch={setTagMatch}
                excludeTags={excludeTags}
                onChangeExcludeTags={setExcludeTags}
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button color="gray" outline onClick={onClose}>
                Cancel
              </Button>
              <Button color="blue" onClick={onGenerate} disabled={previewing || !targets.length}>
                {email
                  ? `Send ${targets.length.toLocaleString()} Notice${targets.length === 1 ? '' : 's'}`
                  : `File ${targets.length.toLocaleString()} Notice${targets.length === 1 ? '' : 's'}`}
              </Button>
            </div>
          </>
        )}

        {phase === 'processing' && (
          <>
            <p className="mb-4 text-sm text-gray-500">
              {queuedBehind >= 0
                ? queuedBehind > 0
                  ? `Waiting for ${queuedBehind} earlier run to finish. Portfolio runs go one at a time so they cannot slow each other, or the rest of the app, down. `
                  : 'Waiting to start. '
                : ''}
              {email ? 'Sending' : 'Filing'} rate and payment change notices. Leave this open, or send it to the
              background and carry on working.
            </p>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-shade-blue transition-all duration-300"
                style={{ width: progress.total ? `${pct}%` : '8%' }}
              />
            </div>
            <p className="mt-2 text-center text-sm text-gray-600">
              {progress.total
                ? `${progress.handled.toLocaleString()} of ${progress.total.toLocaleString()} (${pct}%)`
                : 'Scanning for scheduled changes…'}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button color="gray" outline onClick={onCancelRun} disabled={!jobId}>
                Cancel Run
              </Button>
              <Button color="blue" onClick={onRunInBackground} disabled={!jobId}>
                Run in Background
              </Button>
            </div>
            <p className="mt-2 text-center text-[11px] text-gray-400">
              Cancelling stops between letters. Notices already sent or filed are kept.
            </p>
          </>
        )}

        {phase === 'done' && (
          <>
            <p className="mb-3 text-sm text-gray-600">
              <span className="font-semibold text-green-700">{generated.length}</span> {email ? 'sent' : 'filed'}
              {' · '}
              <span className="font-semibold text-amber-700">{skipped.length}</span> skipped
            </p>
            <div className="flex-1 overflow-auto">
              {generated.length > 0 && (
                <div className="mb-4">
                  <div className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    {email ? 'Sent' : 'Filed'} ({generated.length})
                  </div>
                  <div className="overflow-hidden rounded border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Loan #</th>
                          <th className="px-3 py-1.5 text-left">Borrower</th>
                          <th className="px-3 py-1.5 text-left">Covers</th>
                          <th className="px-3 py-1.5 text-left">Emailed To</th>
                          <th className="px-3 py-1.5 text-right">Effective</th>
                        </tr>
                      </thead>
                      <tbody>
                        {generated.map((g: any, i: number) => (
                          <tr key={`${g.loanNumber}-${g.effectiveDate}-${i}`} className="border-t border-slate-100">
                            <td className="px-3 py-1.5">{g.loanNo}</td>
                            <td className="px-3 py-1.5">{g.borrower}</td>
                            <td className="px-3 py-1.5">{(g.fields || []).join(', ')}</td>
                            {/* Blank on a run that only filed the letters. */}
                            <td className="px-3 py-1.5 text-gray-500">{g.emailedTo || ''}</td>
                            <td className="px-3 py-1.5 text-right">{g.effectiveDate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {skipped.length > 0 && (
                <div>
                  <div className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Skipped ({skipped.length})
                  </div>
                  <div className="overflow-hidden rounded border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Loan #</th>
                          <th className="px-3 py-1.5 text-left">Borrower</th>
                          <th className="px-3 py-1.5 text-left">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {skipped.map((s: any, i: number) => (
                          <tr key={`${s.loanNumber}-${i}`} className="border-t border-slate-100">
                            <td className="px-3 py-1.5">{s.loanNo || s.loanNumber}</td>
                            <td className="px-3 py-1.5">{s.borrower}</td>
                            <td className="px-3 py-1.5 text-amber-700">{s.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {!generated.length && !skipped.length && (
                <p className="text-sm text-gray-400">No loan had a change needing a notice in scope.</p>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              {generated.length > 0 && (
                <Button color="gray" outline onClick={onDownload} loading={downloading}>
                  Download Combined PDF
                </Button>
              )}
              <Button color="blue" onClick={onClose}>
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
